// src/pages/AuthorLanding.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, orderBy, where, Query, DocumentData, Timestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../../services/firebase';

interface DocumentItem {
  id: string;
  title: string;
  updatedAt?: Timestamp;
}

const AuthorLanding: React.FC = () => {
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const authInstance = getAuth();
    const user = authInstance.currentUser;

    if (!user) {
      setError('You must be logged in to view your narratives.');
      setLoading(false);
      return;
    }

    const fetchDocuments = async () => {
      try {
        const idTokenResult = await user.getIdTokenResult(true);
        const isAdmin = idTokenResult.claims.admin === true;
        const isAuthor = idTokenResult.claims.author === true;

        let documentsQuery: Query<DocumentData, DocumentData> = collection(db, 'documents');

        if (isAdmin) {
          // Admins can see all documents
          documentsQuery = query(collection(db, 'documents'), orderBy('updatedAt', 'desc'));
        } else if (isAuthor) {
          // Authors can only see their own documents
          documentsQuery = query(collection(db, 'documents'), where('authorId', '==', user.uid), orderBy('updatedAt', 'desc'));
        } else {
          setError('You do not have permission to view narratives.');
          setLoading(false);
          return;
        }

        const querySnapshot = await getDocs(documentsQuery);
        const docsList: DocumentItem[] = querySnapshot.docs.map(docSnap => ({
          id: docSnap.id,
          title: docSnap.data().title || 'Untitled',
          updatedAt: docSnap.data().updatedAt,
        }));

        setDocuments(docsList);
      } catch (err: unknown) {
        console.error('Error loading documents:', err);
        setError('Could not load your documents.');
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, []);

  // Format Firestore timestamp safely
  const formatDate = (timestamp?: Timestamp): string => {
    if (!timestamp || typeof timestamp.toDate !== 'function') return '';
    return timestamp.toDate().toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-12 text-center text-gray-500 font-medium">
        Loading your narratives...
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-8 text-center">
        <p className="text-red-600 font-medium mb-4">{error}</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-red-50 text-red-600 font-medium rounded-md hover:bg-red-100 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-10">
      {/* Header + CTA */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8 pb-6 border-b border-gray-200">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">My Narratives</h1>
          <p className="text-sm text-gray-500 mt-1">Manage and edit your stories and documents</p>
        </div>

        <Link
          to="/author/new"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white font-medium text-sm rounded-lg shadow-sm hover:bg-indigo-700 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 shrink-0"
        >
          {/* Explicit width/height on SVG prevents gigantic blowing up */}
          <svg
            style={{ width: '18px', height: '18px' }}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          <span>Create a New Document</span>
        </Link>
      </div>

      {/* Document List Section */}
      {documents.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-2xl border border-dashed border-gray-300">
          <p className="text-gray-600 text-lg font-medium mb-1">No narratives found</p>
          <p className="text-gray-500 text-sm mb-6">Start by creating your first document above.</p>
          <Link
            to="/author/new"
            className="inline-flex items-center gap-2 text-indigo-600 font-semibold hover:text-indigo-800 text-sm"
          >
            + Create Document
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {documents.map((doc) => (
            <div
              key={doc.id}
              className="group flex items-center justify-between p-5 bg-white border border-gray-200 rounded-xl hover:border-indigo-300 hover:shadow-sm transition-all duration-200"
            >
              {/* Left Column: Details */}
              <div className="space-y-1.5 pr-4">
                <Link
                  to={`/author/${doc.id}`}
                  className="text-lg font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors line-clamp-1"
                >
                  {doc.title}
                </Link>

                <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                  <span className="font-mono bg-gray-100 px-2 py-0.5 rounded text-gray-600 select-all">
                    ID: {doc.id}
                  </span>
                  {doc.updatedAt && (
                    <>
                      <span>•</span>
                      <span>Updated {formatDate(doc.updatedAt)}</span>
                    </>
                  )}
                </div>
              </div>

              {/* Right Column: Edit CTA */}
              <Link
                to={`/author/${doc.id}/edit`}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-gray-50 hover:bg-indigo-50 text-gray-700 hover:text-indigo-600 font-medium text-sm rounded-md border border-gray-200 hover:border-indigo-200 transition-colors shrink-0"
              >
                <svg
                  style={{ width: '16px', height: '16px' }}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 20h9" />
                  <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                </svg>
                <span>Edit</span>
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AuthorLanding;